import { cn } from '@/lib/cn';
import { convertSecondsToTime } from '@/models/timerModel';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@heidi/design-system/hover-card';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';
import { ExternalLink, Pause, Square } from 'lucide-react';
import { useState } from 'react';
import { IconButtonWithTooltip, VoiceIndicator, iconBtnClass } from './shared';

interface DefaultTileProps {
  readonly isSidebarOpen: boolean;
  readonly isRecording: boolean;
  readonly seconds: number;
  readonly displayName: string;
  readonly showControls: boolean;
  readonly pauseLabel: string;
  readonly stopLabel: string;
  readonly returnLabel: string;
  readonly onNavigate: () => void;
  readonly onPause: (e: React.MouseEvent) => void;
  readonly onStop: (e: React.MouseEvent) => void;
}

/** Original tile for web / desktop mic/speaker recordings. */
export function DefaultSessionTile({
  isSidebarOpen,
  isRecording,
  seconds,
  displayName,
  showControls,
  pauseLabel,
  stopLabel,
  returnLabel,
  onNavigate,
  onPause,
  onStop,
}: DefaultTileProps) {
  const [isHovered, setIsHovered] = useState(false);

  if (!isSidebarOpen) {
    return (
      <div className="mt-auto flex w-full justify-center px-1 pb-2">
        <HoverCard openDelay={100} closeDelay={200}>
          <HoverCardTrigger asChild>
            <button
              onClick={onNavigate}
              className={cn(
                'flex size-8 items-center justify-center rounded-md',
                'bg-surface-accent transition-colors duration-200 hover:bg-surface-accent-hover'
              )}
              aria-label={returnLabel}
            >
              <VoiceIndicator isRecording={isRecording} className="shrink-0" />
            </button>
          </HoverCardTrigger>
          <HoverCardContent
            side="right"
            align="end"
            sideOffset={8}
            className="w-auto min-w-[180px] border-0 bg-surface-accent p-0"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={onNavigate}
              onKeyDown={(e) =>
                e.target === e.currentTarget &&
                (e.key === 'Enter' || e.key === ' ') &&
                onNavigate()
              }
              className="cursor-pointer rounded-[inherit] bg-surface-accent transition-colors hover:bg-surface-accent-hover"
            >
              <div className="px-3 pt-2">
                <TypographyCaptionBold className="text-text-primary">
                  {displayName}
                </TypographyCaptionBold>
              </div>
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <TypographyCaption className="text-text-secondary">
                  {convertSecondsToTime(seconds, true)}
                </TypographyCaption>
                {showControls ? (
                  <div className="flex items-center gap-3">
                    <IconButtonWithTooltip
                      onClick={onPause}
                      ariaLabel={pauseLabel}
                      tooltipLabel={pauseLabel}
                    >
                      <Pause className="size-4 fill-current" />
                    </IconButtonWithTooltip>
                    <IconButtonWithTooltip
                      onClick={onStop}
                      ariaLabel={stopLabel}
                      tooltipLabel={stopLabel}
                    >
                      <Square className="size-4 fill-current" />
                    </IconButtonWithTooltip>
                  </div>
                ) : (
                  <ExternalLink className="size-3.5 text-text-secondary" />
                )}
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      </div>
    );
  }

  if (!showControls) {
    return (
      <div className="mt-auto px-2 pb-2">
        <div
          role="button"
          tabIndex={0}
          onClick={onNavigate}
          onKeyDown={(e) =>
            e.target === e.currentTarget &&
            (e.key === 'Enter' || e.key === ' ') &&
            onNavigate()
          }
          className={cn(
            'flex w-full items-center gap-2 rounded-md bg-surface-accent px-3 py-2',
            'cursor-pointer transition-colors duration-200 ease-out hover:bg-surface-accent-hover'
          )}
          aria-label={returnLabel}
        >
          <VoiceIndicator isRecording={isRecording} className="shrink-0" />
          <TypographyCaptionBold className="min-w-0 flex-1 truncate text-text-primary">
            {displayName}
          </TypographyCaptionBold>
          <ExternalLink className="size-3.5 shrink-0 text-text-secondary" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-auto px-2 pb-2">
      <div
        role="button"
        tabIndex={0}
        onClick={onNavigate}
        onKeyDown={(e) =>
          e.target === e.currentTarget &&
          (e.key === 'Enter' || e.key === ' ') &&
          onNavigate()
        }
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={cn(
          'flex w-full flex-col-reverse overflow-hidden rounded-md bg-surface-accent',
          'cursor-pointer text-left transition-all duration-200 ease-out',
          isHovered && 'bg-surface-accent-hover'
        )}
        aria-label={returnLabel}
      >
        <div className="relative px-3 py-2">
          <div
            className={cn(
              'flex w-full items-center gap-1 transition-opacity duration-200 ease-out',
              isHovered ? 'pointer-events-none opacity-0' : 'opacity-100'
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <VoiceIndicator isRecording={isRecording} className="shrink-0" />
              <TypographyCaptionBold className="min-w-0 flex-1 truncate text-text-primary">
                {displayName}
              </TypographyCaptionBold>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <button onClick={onPause} aria-label={pauseLabel} className={iconBtnClass}>
                <Pause className="size-4 fill-current" />
              </button>
              <button onClick={onStop} aria-label={stopLabel} className={iconBtnClass}>
                <Square className="size-4 fill-current" />
              </button>
            </div>
          </div>
          <div
            className={cn(
              'absolute inset-0 flex items-center justify-between px-3 py-2 transition-opacity duration-200 ease-out',
              isHovered ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          >
            <TypographyCaption className="text-text-secondary">
              {convertSecondsToTime(seconds, true)}
            </TypographyCaption>
            <div className="flex shrink-0 items-center gap-3">
              <IconButtonWithTooltip
                onClick={onPause}
                ariaLabel={pauseLabel}
                tooltipLabel={pauseLabel}
              >
                <Pause className="size-4 fill-current" />
              </IconButtonWithTooltip>
              <IconButtonWithTooltip
                onClick={onStop}
                ariaLabel={stopLabel}
                tooltipLabel={stopLabel}
              >
                <Square className="size-4 fill-current" />
              </IconButtonWithTooltip>
            </div>
          </div>
        </div>
        <div
          className={cn(
            'overflow-hidden transition-all duration-200 ease-out',
            isHovered ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="flex items-center gap-2 px-3 pt-2">
            <VoiceIndicator isRecording={isRecording} className="shrink-0" />
            <TypographyCaptionBold className="min-w-0 flex-1 truncate text-text-primary">
              {displayName}
            </TypographyCaptionBold>
          </div>
        </div>
      </div>
    </div>
  );
}
